import moment, { ISO_8601, Moment } from 'moment';
import { from, Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import {
  AccountId,
  Count,
  UserId,
  User as UserT,
  Squad as SquadT,
} from 'api/models';
import { Project } from 'api/project';
import { pushes, UserChannel, EventType, ObjectType, Action } from 'api/pushes';
import { TaskType } from 'api/tasks';
import { NotificationCount } from 'stores/notifications/models';
import { axios } from '../axios';
import {
  configuration,
  customerConfiguration,
  Configuration as CT,
  ChannelConfiguration as CCT,
  Channel,
} from './configuration';
import { NotificationMeta } from './meta';
import {
  UserReference,
  TaskReference,
  DocumentReference,
  ProjectReference,
  ProjectConversationReference,
  StatusUpdateReference,
  SpaceTabReference,
  ApprovalReference,
  UserTimesheetReference,
  TimeOffReference,
  FinancialsReference,
  AccountsReference,
  ResourceRequestReference,
} from './references';

export type ContainerReference =
  | TaskReference
  | DocumentReference
  | ProjectReference
  | ProjectConversationReference
  | StatusUpdateReference
  | SpaceTabReference
  | ApprovalReference
  | UserTimesheetReference
  | TimeOffReference
  | FinancialsReference
  | AccountsReference
  | ResourceRequestReference;

export interface Notification {
  id: string;
  recipient: UserReference;
  container: ContainerReference;
  timestamp: string;
  meta: NotificationMeta;
  sortKey: string;
}

interface NotificationGroupBase {
  notifications: Notification[];
}

interface Identifiable<T> {
  id: T;
}

interface Named {
  name: string;
}

interface Shareable {
  private: boolean;
}

interface Deleted {
  deleted?: boolean;
}

interface Archived {
  archived?: boolean;
}

interface StatusValue {
  statusValue?: number;
}

export interface ProjectPreview
  extends Identifiable<number>,
    Named,
    Deleted,
    Archived,
    StatusValue,
    Shareable {}

export interface ProjectPhasePreview
  extends Identifiable<number>,
    Deleted,
    StatusValue,
    Named {}

export interface TaskPreview extends Identifiable<number>, Named, Shareable {
  assignee?: SquadT;
  status: number;
  progress?: number;
  type: TaskType;
  atRisk?: boolean;
  priority?: number;
  dueDate?: string | null;
  project: ProjectPreview;
  projectPhase?: ProjectPhasePreview;
  formInstanceId?: number;
}

export interface SpacePreview extends Identifiable<number>, Named {}

export interface DocumentPreview
  extends Identifiable<number>,
    Partial<Named>,
    Shareable {
  project: ProjectPreview;
  space: SpacePreview;
  tabId: number;
  collaborators: UserT[];
}

export interface TaskNotificationGroup extends NotificationGroupBase {
  key: TaskReference;
  task: TaskPreview;
}

export interface DocumentNotificationGroup extends NotificationGroupBase {
  key: DocumentReference;
  document: DocumentPreview;
}

export interface ProjectNotificationGroup extends NotificationGroupBase {
  key: ProjectReference;
  project: Project;
}
export interface ProjectConversationPreview
  extends Identifiable<number>,
    Partial<Named>,
    Shareable {
  project: ProjectPreview;
  memberIds: number[];
  conversationId: number;
  conversationName: string;
}
export interface ProjectConversationNotificationGroup
  extends NotificationGroupBase {
  key: ProjectConversationReference;
  conversation: ProjectConversationPreview;
}

export interface StatusUpdatePreview extends Identifiable<number>, Named {
  project: ProjectPreview;
  autoPublished?: boolean;
}

export interface StatusUpdateNotificationGroup extends NotificationGroupBase {
  key: StatusUpdateReference;
  statusUpdate: StatusUpdatePreview;
}

export enum SpaceTabType {
  RocketlaneDocument = 'ROCKETLANE_DOCUMENT',
}

export interface SpaceTabPreview extends Identifiable<number>, Partial<Named> {
  logoUrl?: string;
  type?: SpaceTabType;
  project: ProjectPreview;
  space: SpacePreview;
}

export interface SpaceTabNotificationGroup extends NotificationGroupBase {
  key: SpaceTabReference;
  spaceTab: SpaceTabPreview;
}

interface ApprovalPreviewBase extends Identifiable<number>, Shareable {
  approverIds: number[];
  actedUpon: boolean;
  dueDate?: string;
}

export enum ApprovalObjectType {
  Task = 'Task',
  Document = 'Document',
}

export interface TaskApprovalPreview extends ApprovalPreviewBase {
  approvalObjectType: ApprovalObjectType.Task;
  task: TaskPreview;
}

export interface DocumentApprovalPreview extends ApprovalPreviewBase {
  approvalObjectType: ApprovalObjectType.Document;
  document: DocumentPreview;
}

export type ApprovalPreview = TaskApprovalPreview | DocumentApprovalPreview;

export interface ApprovalNotificationGroup extends NotificationGroupBase {
  key: ApprovalReference;
  approval: ApprovalPreview;
}

export interface UserTimesheetPreview {
  userId: number;
  approverIds: number[];
}

export interface UserTimesheetNotificationGroup extends NotificationGroupBase {
  key: UserTimesheetReference;
  timesheet: UserTimesheetPreview;
}

export interface TimeoffNotificationGroup extends NotificationGroupBase {
  key: TimeOffReference;
  timeOff: {
    accountId: number;
    userId: number;
  };
}

export interface ResourceRequestNotificationGroup
  extends NotificationGroupBase {
  key: ResourceRequestReference;
  resourceRequest: {
    resourceRequestMappingId: number;
  };
}

export interface FinancialsNotificationGroup extends NotificationGroupBase {
  key: FinancialsReference;
  timeOff: {
    accountId: number;
    userId: number;
  };
}

export interface AccountsNotificationGroup extends NotificationGroupBase {
  key: AccountsReference;
  company: {
    id: number;
    name: string;
    type: string;
    companyUrl?: string;
  };
}

export type NotificationGroup =
  | TaskNotificationGroup
  | DocumentNotificationGroup
  | ProjectNotificationGroup
  | ProjectConversationNotificationGroup
  | StatusUpdateNotificationGroup
  | SpaceTabNotificationGroup
  | ApprovalNotificationGroup
  | TimeoffNotificationGroup
  | UserTimesheetNotificationGroup
  | FinancialsNotificationGroup
  | AccountsNotificationGroup
  | ResourceRequestNotificationGroup;

export const NotificationGroup = {
  when:
    <T>(
      task: (group: TaskNotificationGroup) => T,
      document: (group: DocumentNotificationGroup) => T,
      project: (group: ProjectNotificationGroup) => T,
      conversation: (group: ProjectConversationNotificationGroup) => T,
      statusUpdate: (group: StatusUpdateNotificationGroup) => T,
      spaceTab: (group: SpaceTabNotificationGroup) => T,
      approval: (group: ApprovalNotificationGroup) => T,
      timesheet: (group: UserTimesheetNotificationGroup) => T,
      timeoff: (group: TimeoffNotificationGroup) => T,
      financials: (group: FinancialsNotificationGroup) => T,
      accounts: (group: AccountsNotificationGroup) => T,
      otherwise: (group: NotificationGroup) => T,
      resourceRequest: (group: ResourceRequestNotificationGroup) => T,
    ) =>
    (group: NotificationGroup) => {
      const typeless = group as any;

      if (typeless.task != null) {
        return task(typeless);
      }
      if (typeless.document != null) {
        return document(typeless);
      }
      if (typeless.project != null) {
        return project(typeless);
      }
      if (typeless.conversation != null) {
        return conversation(typeless);
      }
      if (typeless.statusUpdate != null) {
        return statusUpdate(typeless);
      }
      if (typeless.spaceTab != null) {
        return spaceTab(typeless);
      }
      if (typeless.approval != null) {
        return approval(typeless);
      }
      if (typeless.timesheet != null) {
        return timesheet(typeless);
      }
      if (typeless.timeOff != null) {
        return timeoff(typeless);
      }
      if (typeless.projects != null) {
        return financials(typeless);
      }
      if (typeless.company != null) {
        return accounts(typeless);
      }
      if (typeless.resourceRequest != null) {
        return resourceRequest(typeless);
      }

      return otherwise(typeless);
    },
};

export enum NotificationStatus {
  New = 'New',
  Cleared = 'Cleared',
}

interface LastSeenAt {
  lastSeenAt: string;
}

export type Start = ['start', string];
export const Start = (start?: string): Start => [
  'start',
  start || (new Date().getTime() * 1000).toString(),
];

export enum Filter {
  All = 'All',
  AssignedToMe = 'AssignedToMe',
  AssignedToTeam = 'AssignedToTeam',
  Mentions = 'Mentions',
}

// handles all the api calls for notifications and notification groups
export const notifications = {
  get: (
    { uri }: ContainerReference,
    status: NotificationStatus,
    filter: Filter,
    [, count]: NotificationCount,
    [, start]: Start,
  ): Observable<Notification[]> =>
    axios().pipe(
      take(1),
      switchMap(({ get }) =>
        from(
          get<Notification[]>(
            `/notifications?container=${uri}&status=${status}&count=${count}&start=${start}&filter=${filter}`,
          ),
        ),
      ),
      map((response) => response.data),
    ),
  groups: (
    status: NotificationStatus,
    filter: Filter,
    [, count]: Count,
    [, start]: Start,
    [, size]: NotificationCount,
    exclusions: ContainerReference[] = [],
  ): Observable<NotificationGroup[]> =>
    // fetch the notifications based on the filter and status
    axios().pipe(
      take(1),
      switchMap(({ get }) =>
        from(
          get<NotificationGroup[]>(
            '/notifications/groups' +
              `?status=${status}` +
              `&count=${count}` +
              `&start=${start}` +
              `&groupSize=${size}` +
              `&filter=${filter}` +
              `&exclusions=${exclusions.map((e) => e.uri).join(',')}`,
          ),
        ),
      ),
      map((response) => response.data),
    ),
  unclear: (key: ContainerReference, filter: Filter): Observable<number> =>
    axios().pipe(
      take(1),
      switchMap(({ patch }) =>
        from(
          patch<number>(
            `/notifications?container=${key.uri}&filter=${filter}`,
            {
              status: NotificationStatus.New,
            },
          ),
        ),
      ),
      map((response) => response.data),
    ),
  clear: (key: ContainerReference, filter: Filter): Observable<number> =>
    axios().pipe(
      take(1),
      switchMap(({ patch }) =>
        from(
          patch<number>(
            `/notifications?container=${key.uri}&filter=${filter}`,
            {
              status: NotificationStatus.Cleared,
            },
          ),
        ),
      ),
      map((response) => response.data),
    ),
  updates: (accountId: AccountId, userId: UserId): Observable<Notification> =>
    // subscribe to the notifications channel for the user and account id
    // and map the messages to the notification type
    pushes<Notification>(
      UserChannel(accountId, userId),
      EventType(Action.Create, ObjectType.Notification),
    ).pipe(map(({ data }) => data)),

  // get the last seen at for the user and account id from the server
  // and return it as a stream of updates
  lastSeenAt: (): Observable<Moment> =>
    axios().pipe(
      take(1),
      switchMap(({ get }) =>
        from(get<LastSeenAt>('/notifications/last-seen-at')),
      ),
      map((response) => response.data.lastSeenAt),
      map((date) => moment(date, ISO_8601)),
    ),

  // update the last seen at date to the current time
  seen: (): Observable<void> =>
    axios().pipe(
      take(1),
      switchMap(({ post }) =>
        from(
          post<LastSeenAt, void>('/notifications/last-seen-at', {
            lastSeenAt: moment().toISOString(),
          }),
        ),
      ),
    ),
  configuration,
  customerConfiguration,
};

export type User = UserT;
export type Squad = SquadT;
export type Configuration = CT;
export type ChannelConfiguration = CCT;
export { Channel };
